import useAuth from 'common/useAuth';
import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';

import type { RecentHistoryEntry, RecentHistoryScope } from './recentHistory';
import { createRecentHistory } from './recentHistory';

interface RecentHistoryContextValue {
  entries: readonly RecentHistoryEntry[];
  recordVisit: (entry: Omit<RecentHistoryEntry, 'visitedAt'>) => void;
  clearHistory: () => void;
}

const RecentHistoryContext = createContext<RecentHistoryContextValue | undefined>(undefined);
const inactiveHistory: RecentHistoryContextValue = {
  entries: [],
  recordVisit: () => {},
  clearHistory: () => {}
};

/** Own one store and its reactive snapshot for the lifetime of an authenticated scope. */
const ScopedRecentHistoryProvider = ({
  children,
  companyId,
  userId
}: PropsWithChildren<RecentHistoryScope>) => {
  const [store] = useState(() => createRecentHistory({ companyId, userId }));
  const [entries, setEntries] = useState(() => store.read());
  const { pathname } = useLocation();
  const activeVisit = useRef<Omit<RecentHistoryEntry, 'visitedAt'>>();

  // Routed details can remount when their subpage changes. Keep the visit guard
  // above that boundary and reset it only after leaving the canonical detail.
  useEffect(() => {
    const visit = activeVisit.current;
    if (visit && pathname !== visit.path && !pathname.startsWith(`${visit.path}/`)) {
      activeVisit.current = undefined;
    }
  }, [pathname]);

  const recordVisit = useCallback(
    (entry: Omit<RecentHistoryEntry, 'visitedAt'>) => {
      if (activeVisit.current?.type === entry.type && activeVisit.current.id === entry.id) return;
      activeVisit.current = entry;
      setEntries(store.record(entry));
    },
    [store]
  );
  const clearHistory = useCallback(() => setEntries(store.clear()), [store]);
  const value = useMemo(
    () => ({ entries, recordVisit, clearHistory }),
    [entries, recordVisit, clearHistory]
  );

  return <RecentHistoryContext.Provider value={value}>{children}</RecentHistoryContext.Provider>;
};

/**
 * Share recent details across the private shell and routed pages.
 * Until authentication supplies both IDs, expose an empty history with inert actions.
 * A scope key replaces the store and snapshot together on account/company changes,
 * so consumers never render the previous identity's history during an effect reset.
 * Logout discards the in-memory store while preserving that user's persisted visits.
 */
const RecentHistoryProvider = ({ children }: PropsWithChildren) => {
  const { authenticated, user } = useAuth();
  const companyId = user?.companyId;
  const userId = user?.id;

  if (!authenticated || !companyId?.trim() || !userId?.trim()) {
    return (
      <RecentHistoryContext.Provider value={inactiveHistory}>
        {children}
      </RecentHistoryContext.Provider>
    );
  }

  return (
    <ScopedRecentHistoryProvider
      key={JSON.stringify([companyId, userId])}
      companyId={companyId}
      userId={userId}
    >
      {children}
    </ScopedRecentHistoryProvider>
  );
};

/** Read the reactive list or record/clear visits from a descendant of the provider. */
export const useRecentHistory = () => {
  const context = useContext(RecentHistoryContext);
  if (!context) throw new Error('useRecentHistory must be used within RecentHistoryProvider');
  return context;
};

export default RecentHistoryProvider;
